import React from "react";
import { css } from "@emotion/core";

import { Field, Typography, useTheme } from "@zapier/design-system";

type PaymentFormFieldProps = {
  /** Children to render inside field container */
  children?: React.ReactNode;
  /** Label id */
  id?: string;
  /** Hide field */
  isHidden?: boolean;
  /** Wraps children in div instead of label, used for radio and checkbox groups */
  isGroup?: boolean;
  /** Field label */
  label?: string;
  /** Spacing for component */
  noMargin?: boolean;
  /** Number of grid columns to span */
  span?: number;
  /** Whether or not to display a required label */
  required?: boolean;
};

const styles = {
  container: (props: PaymentFormFieldProps, isRebrand?: boolean) =>
    css(
      {
        gridColumn: props.span ? `span ${props.span}` : "auto",
      },
      !props.noMargin && {
        marginBottom: isRebrand ? 8 : 20,
      }
    ),
  label: (props: PaymentFormFieldProps) => ({
    ...Typography.paragraph3,
    fontWeight: 600,
    marginBottom: 10,
    display: !props.label ? "none" : "inherit",
  }),
};

/* Fixes the grid within a grid problem for Field component sizing, specifically for the State & Zip bank fields */
const fieldWrapperCss = css`
  & > div {
    grid-template-columns: minmax(0, 1fr) !important;
  }
`;

const PaymentFormField = ({ children, ...props }: PaymentFormFieldProps) => {
  const { isRebrand } = useTheme();
  const Container = props.isGroup ? "div" : "label";

  if (isRebrand) {
    return (
      <Container
        aria-label={props.id}
        css={styles.container(props, isRebrand)}
        hidden={props.isHidden}
        id={props.id}
      >
        <div css={fieldWrapperCss}>
          <Field
            isRequired={props.required}
            label={props.label ? props.label : ""}
            renderInput={() => children}
          />
        </div>
      </Container>
    );
  }
  return (
    <Container
      aria-label={props.id}
      css={styles.container(props)}
      hidden={props.isHidden}
      id={props.id}
    >
      <div css={styles.label(props)}>{props.label}</div>
      {children}
    </Container>
  );
};

export default PaymentFormField;
